import "../styles/CashierPage.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { apiUrl, socketUrl } from "../helper";
import { parseArticleKit, isKitArticle } from "../articleKit";
import ArticleImage from "./ArticleImage";

const useTouchScroll = (ref, deps = []) => {
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        let startY = 0;
        let startScroll = 0;
        let dragging = false;

        const onStart = (event) => {
            if (!event.touches || event.touches.length !== 1) return;
            dragging = true;
            startY = event.touches[0].clientY;
            startScroll = el.scrollTop;
        };

        const onMove = (event) => {
            if (!dragging || !event.touches || event.touches.length !== 1) return;
            const delta = startY - event.touches[0].clientY;
            if (Math.abs(delta) > 4) {
                el.scrollTop = startScroll + delta;
                event.preventDefault();
            }
        };

        const onEnd = () => {
            dragging = false;
        };

        el.addEventListener("touchstart", onStart, { passive: true });
        el.addEventListener("touchmove", onMove, { passive: false });
        el.addEventListener("touchend", onEnd, { passive: true });
        el.addEventListener("touchcancel", onEnd, { passive: true });

        return () => {
            el.removeEventListener("touchstart", onStart);
            el.removeEventListener("touchmove", onMove);
            el.removeEventListener("touchend", onEnd);
            el.removeEventListener("touchcancel", onEnd);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, ...deps]);
};

const CashierPage = () => {
    const [selectedCategory, setSelectedCategory] = useState(0);
    const [categories, setCategories] = useState([]);
    const [pairingCode, setPairingCode] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [printQtyModal, setPrintQtyModal] = useState(null); // { cIndex, aIndex, name, qty }
    const [alertSending, setAlertSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [syncInfo, setSyncInfo] = useState(null);
    const [showSyncPanel, setShowSyncPanel] = useState(false);
    const [pendingTickets, setPendingTickets] = useState([]);
    const [syncRetrying, setSyncRetrying] = useState(false);
    const scrollRef = useRef(null);

    useTouchScroll(scrollRef, [selectedCategory]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [selectedCategory]);

    const scrollArticles = (direction) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ top: direction * Math.max(220, el.clientHeight * 0.7), behavior: "smooth" });
    };

    const total = useMemo(
        () => categories.reduce(
            (acc, category) => acc + category.articles.reduce(
                (articleAcc, article) => articleAcc + article.amount * article.price,
                0,
            ),
            0,
        ),
        [categories],
    );

    const categoriesRef = useRef([]);
    useEffect(() => {
        categoriesRef.current = categories;
    }, [categories]);

    const loadCatalog = useCallback(async ({ preserveCart = false } = {}) => {
        try {
            const response = await axios.get(`${apiUrl}/api/catalog`, { timeout: 8000 });
            const remoteCategories = response.data.categories || [];

            const amounts = new Map();
            if (preserveCart) {
                categoriesRef.current.forEach((category) => {
                    category.articles.forEach((article) => {
                        if (article.amount > 0) {
                            amounts.set(article.id, article.amount);
                        }
                    });
                });
            }

            const nextCategories = remoteCategories.map((category) => ({
                ...category,
                articles: category.articles.map((article) => ({
                    ...article,
                    amount: amounts.get(article.id) || 0,
                })),
            }));

            // Ne pas vider l'écran si le catalogue local est temporairement vide
            if (nextCategories.length === 0 && categoriesRef.current.length > 0 && preserveCart) {
                setLoading(false);
                return categoriesRef.current;
            }

            setCategories(nextCategories);
            if (!preserveCart) {
                setSelectedCategory(0);
            }
            setLoading(false);
            return nextCategories;
        } catch (_error) {
            setLoading(false);
            // Garde le panier / catalogue déjà affiché hors ligne
            if (categoriesRef.current.length === 0) {
                setError("Catalogue indisponible — mode hors ligne");
            }
            return categoriesRef.current;
        }
    }, []);

    const generateCode = async () => {
        const response = await axios.get(`${apiUrl}/pairing-code`);
        return response.data.code;
    };

    const fetchPairingCode = async (force = false) => {
        let code;
        const urlCode = new URLSearchParams(window.location.search).get("code");

        if (urlCode && /^\d{6}$/.test(urlCode)) {
            code = urlCode;
            localStorage.setItem("pairingCode", code);
        } else if (localStorage.getItem("pairingCode") && !force) {
            code = localStorage.getItem("pairingCode");
        } else {
            code = await generateCode();
            localStorage.setItem("pairingCode", code);
        }

        setPairingCode(code);
        return code;
    };

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        onMessage: (event) => {
            let data;
            try {
                data = JSON.parse(event.data);
            } catch (_error) {
                return;
            }
            if (data.action === "catalog-updated") {
                loadCatalog({ preserveCart: true }).then((cats) => {
                    if (!cats?.length) return;
                    const code = localStorage.getItem("pairingCode");
                    if (!code) return;
                    sendMessage(JSON.stringify({
                        action: "refresh-categories",
                        categories: cats,
                        code,
                    }));
                }).catch(() => {});
            }
            if (data.action === "reprint" && data.invoiceId) {
                const key = `reprint:${data.invoiceId}`;
                if (sessionStorage.getItem(key)) return;
                sessionStorage.setItem(key, String(Date.now()));
                window.location.href = `/invoice/${data.invoiceId}`;
            }
        },
        shouldReconnect: () => true,
        reconnectAttempts: 50,
        reconnectInterval: 2000,
    });

    useEffect(() => {
        loadCatalog().catch(() => setError("Impossible de charger le catalogue"));
    }, [loadCatalog]);

    // Filet de sécurité : rafraîchir les stocks même sans événement WS
    useEffect(() => {
        const timer = setInterval(() => {
            loadCatalog({ preserveCart: true }).catch(() => {});
        }, 12000);
        return () => clearInterval(timer);
    }, [loadCatalog]);

    useEffect(() => {
        let active = true;

        const pollSync = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/sync/status`);
                if (active && response.data?.sync) {
                    setSyncInfo(response.data.sync);
                }
            } catch (_error) {
                if (active) {
                    setSyncInfo(null);
                }
            }
        };

        pollSync();
        const timer = setInterval(pollSync, 5000);
        return () => {
            active = false;
            clearInterval(timer);
        };
    }, []);

    const openSyncPanel = async () => {
        setShowSyncPanel(true);
        try {
            const response = await axios.get(`${apiUrl}/api/sync/pending`);
            setPendingTickets(response.data.pending || []);
            if (response.data.sync) setSyncInfo(response.data.sync);
        } catch (_error) {
            setPendingTickets([]);
        }
    };

    const retrySync = async () => {
        setSyncRetrying(true);
        try {
            const response = await axios.post(`${apiUrl}/api/sync/retry`);
            if (response.data.sync) setSyncInfo(response.data.sync);
            const pending = await axios.get(`${apiUrl}/api/sync/pending`);
            setPendingTickets(pending.data.pending || []);
            setError("");
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Échec du renvoi");
        } finally {
            setSyncRetrying(false);
        }
    };

    useEffect(() => {
        let active = true;

        const initPairing = async () => {
            const code = await fetchPairingCode();

            if (!active || readyState !== ReadyState.OPEN) {
                return;
            }

            sendMessage(JSON.stringify({
                action: "pairing",
                code,
            }));
        };

        initPairing();

        return () => {
            active = false;
        };
    }, [readyState, sendMessage]);

    const pushCategories = (nextCategories) => {
        setCategories(nextCategories);
        sendMessage(JSON.stringify({
            action: "refresh-categories",
            categories: nextCategories,
            code: pairingCode,
        }));
    };

    const setArticleAmount = (categoryId, articleId, amount) => {
        const article = categories[categoryId]?.articles[articleId];
        if (!article || !article.available) return;

        const nextAmount = Math.max(0, Number(amount) || 0);
        if (nextAmount > 0 && article.stock !== null && nextAmount > article.stock) {
            setError(`Stock indicatif dépassé : ${article.stock} pour ${parseArticleKit(article.name).displayName}`);
        } else {
            setError("");
        }

        const nextCategories = categories.map((category, cIndex) => ({
            ...category,
            articles: category.articles.map((item, aIndex) => (
                cIndex === categoryId && aIndex === articleId
                    ? { ...item, amount: nextAmount }
                    : item
            )),
        }));

        pushCategories(nextCategories);
    };

    const AddArticle = (categoryId, articleId, amount = 1) => {
        const category = categories[categoryId];
        const article = category?.articles[articleId];
        if (!article || !article.available) return;

        // Ticket Artisan / Tracteur : demander la quantité (→ 1 ticket par stand)
        if (isKitArticle(article, category.stand) && amount > 0) {
            setPrintQtyModal({
                cIndex: categoryId,
                aIndex: articleId,
                name: parseArticleKit(article.name).displayName,
                qty: Math.max(1, article.amount || 1),
            });
            return;
        }

        const nextAmount = Math.max(0, (article.amount || 0) + amount);
        setArticleAmount(categoryId, articleId, nextAmount);
    };

    const confirmPrintQty = () => {
        if (!printQtyModal) return;
        const qty = Math.max(0, Math.min(99, Number(printQtyModal.qty) || 0));
        setArticleAmount(printQtyModal.cIndex, printQtyModal.aIndex, qty);
        setPrintQtyModal(null);
    };

    const SubmitOrder = async () => {
        if (orderedArticles.length === 0) {
            setError("Ajoutez au moins un article");
            return;
        }

        setSubmitting(true);
        setError("");

        try {
            const result = await axios.post(`${apiUrl}/api/invoice`, {
                categories,
                code: pairingCode,
            });

            const invoiceId = result.data.invoice.id;
            window.location.href = `/invoice/${invoiceId}`;
        } catch (requestError) {
            const message = requestError.response?.data?.message || "Erreur lors de la validation";
            setError(message);
            setSubmitting(false);
        }
    };

    const sendAdminAlert = async () => {
        if (!syncInfo?.online || !pairingCode || alertSending) return;
        const confirmed = window.confirm("Prévenir les administrateurs d'un problème sur cette caisse ?");
        if (!confirmed) return;

        setAlertSending(true);
        setError("");
        try {
            const response = await axios.post(`${apiUrl}/api/caisse/alert`, {
                code: pairingCode,
                message: `Problème signalé depuis la caisse ${pairingCode}`,
            }, { timeout: 10000 });
            setError(response.data.message || "Alerte envoyée");
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'envoyer l'alerte");
        } finally {
            setAlertSending(false);
        }
    };

    const orderedArticles = categories.flatMap((category) => (
        category.articles
            .filter((article) => article.amount > 0)
            .map((article) => ({ ...article, categoryId: category.id, categoryStand: category.stand }))
    ));

    const kitCount = orderedArticles.reduce((acc, article) => {
        if (isKitArticle(article, article.categoryStand)) {
            return acc + (article.amount || 0);
        }
        return acc;
    }, 0);

    if (loading) {
        return <div className="cashier-page loading-state">Chargement du catalogue...</div>;
    }

    return (
        <div className="cashier-page">
            <link href="/vendor/bootstrap.min.css" rel="stylesheet" />
            <div className="main">
                <div className="container">
                    <div className="menu card">
                        <ul>
                            {categories.map((category, index) => (
                                <li key={category.id}>
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={() => setSelectedCategory(index)}
                                    >
                                        {category.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {syncInfo?.online && (
                            <button
                                type="button"
                                className="btn-caisse-alert"
                                disabled={alertSending}
                                onClick={sendAdminAlert}
                                title="Prévenir les admins"
                            >
                                {alertSending ? "…" : "Problème"}
                            </button>
                        )}
                    </div>

                    {categories.map((category, cIndex) => (
                        <div
                            id={`category${cIndex}`}
                            className="content card"
                            key={category.id}
                            style={selectedCategory !== cIndex ? { display: "none" } : undefined}
                        >
                            <div className="articles-toolbar">
                                <button
                                    type="button"
                                    className="scroll-btn"
                                    onClick={() => scrollArticles(-1)}
                                    aria-label="Monter"
                                >
                                    ▲
                                </button>
                                <button
                                    type="button"
                                    className="scroll-btn"
                                    onClick={() => scrollArticles(1)}
                                    aria-label="Descendre"
                                >
                                    ▼
                                </button>
                            </div>
                            <div className="articles-scroll" ref={selectedCategory === cIndex ? scrollRef : undefined}>
                                <div className="article-grid">
                                    {category.articles.map((article, aIndex) => (
                                        <button
                                            type="button"
                                            key={article.id}
                                            className={`btn btn-light article card ${!article.available ? "article-disabled" : ""}`}
                                            onClick={() => AddArticle(cIndex, aIndex)}
                                            disabled={!article.available}
                                        >
                                            {article.amount > 0 && (
                                                <span className="article-badge" title={`${article.amount} personne(s)`}>
                                                    {article.amount}
                                                </span>
                                            )}
                                            <div className="article-content">
                                                <ArticleImage src={article.image} name={parseArticleKit(article.name).displayName} />
                                                <span>{parseArticleKit(article.name).displayName}</span>
                                            </div>
                                            <div className="article-price">{article.price.toFixed(2)}€</div>
                                            {isKitArticle(article, category.stand) && (
                                                <div className="article-stock">
                                                    {article.amount > 0
                                                        ? `${article.amount} × (boisson + repas + dessert)`
                                                        : "Choisir la quantité"}
                                                </div>
                                            )}
                                            {article.stock !== null && !isKitArticle(article, category.stand) && (
                                                <div className="article-stock">Stock : {article.stock}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="card footer">
                    <div className="content">
                        {syncInfo && (
                        <button
                            type="button"
                            className={`sync-badge ${syncInfo.online ? "online" : "offline"}`}
                            title={syncInfo.lastError || "État de synchronisation"}
                            onClick={openSyncPanel}
                        >
                            {syncInfo.online ? "En ligne" : "Hors ligne"}
                            {syncInfo.pendingCount > 0
                                ? ` · ${syncInfo.pendingCount} ticket${syncInfo.pendingCount > 1 ? "s" : ""} en attente`
                                : ""}
                        </button>
                        )}
                        <a style={{ marginRight: "10px" }}>
                            Code d&apos;appairage: <strong>{pairingCode}</strong>
                        </a>
                        <a style={{ marginRight: "10px" }}>
                            Total de la commande: <strong>{total.toFixed(2)}</strong>€
                            {kitCount > 0 ? ` · ${kitCount} ticket${kitCount > 1 ? "s" : ""} kit` : ""}
                        </a>
                        <button
                            type="button"
                            className="btn btn-warning"
                            onClick={() => setShowModal(true)}
                            disabled={orderedArticles.length === 0}
                        >
                            Valider la commande
                        </button>
                    </div>
                    {error && <div className="footer-error">{error}</div>}
                </div>
            </div>

            {showSyncPanel && (
                <div className="order-modal-overlay" onClick={() => setShowSyncPanel(false)}>
                    <div className="order-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="order-modal-header">
                            <h1 className="modal-title fs-5">Synchronisation</h1>
                            <button type="button" className="close-btn" onClick={() => setShowSyncPanel(false)}>×</button>
                        </div>
                        <div className="order-modal-body">
                            <p>
                                État : <strong>{syncInfo?.online ? "En ligne" : "Hors ligne"}</strong>
                            </p>
                            {syncInfo?.lastError && (
                                <p className="footer-error">{syncInfo.lastError}</p>
                            )}
                            {pendingTickets.length === 0 ? (
                                <p>Aucun ticket en attente d&apos;envoi.</p>
                            ) : (
                                <ul className="sync-pending-list">
                                    {pendingTickets.map((ticket) => (
                                        <li key={ticket.clientInvoiceId || ticket.id}>
                                            #{ticket.id}
                                            {" · "}
                                            {ticket.createdAt
                                                ? new Date(ticket.createdAt).toLocaleTimeString("fr-FR")
                                                : "—"}
                                            {" · "}
                                            {Number(ticket.total || 0).toFixed(2)}€
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="order-modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={retrySync}
                                disabled={syncRetrying}
                            >
                                {syncRetrying ? "Envoi..." : "Renvoyer maintenant"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="order-modal-overlay" onClick={() => !submitting && setShowModal(false)}>
                    <div className="order-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="order-modal-header">
                            <h1 className="modal-title fs-5">Récapitulatif de la commande</h1>
                            <button type="button" className="close-btn" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <div className="order-modal-body" id="orderContent">
                            {orderedArticles.map((article) => {
                                const categoryIndex = categories.findIndex((c) => c.id === article.categoryId);
                                const articleIndex = categories[categoryIndex].articles.findIndex((a) => a.id === article.id);

                                return (
                                    <div key={`${article.id}-${article.categoryId}`} className="article-line">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-article"
                                            onClick={() => AddArticle(categoryIndex, articleIndex, -1)}
                                        >
                                            -
                                        </button>
                                        <code>
                                            {article.amount}x
                                            {isKitArticle(article, article.categoryStand) ? "" : ""}
                                        </code>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-article"
                                            onClick={() => {
                                                if (isKitArticle(article, article.categoryStand)) {
                                                    AddArticle(categoryIndex, articleIndex, 1);
                                                    return;
                                                }
                                                AddArticle(categoryIndex, articleIndex, 1);
                                            }}
                                        >
                                            +
                                        </button>
                                        <a>{parseArticleKit(article.name).displayName}</a>
                                        <kbd>{article.price.toFixed(2)}€</kbd>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="order-modal-footer">
                            <a style={{ marginRight: "10px" }}>
                                Total de la commande: <strong>{total.toFixed(2)}</strong>€
                            </a>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={SubmitOrder}
                                disabled={submitting || orderedArticles.length === 0}
                            >
                                {submitting ? "Validation..." : "Valider"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {printQtyModal && (
                <div className="order-modal-overlay" onClick={() => setPrintQtyModal(null)}>
                    <div className="order-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="order-modal-header">
                            <h1 className="modal-title fs-5">Quantité — {printQtyModal.name}</h1>
                            <button type="button" className="close-btn" onClick={() => setPrintQtyModal(null)}>×</button>
                        </div>
                        <div className="order-modal-body" style={{ textAlign: "center" }}>
                            <p>Combien de personnes ?<br /><small>Chaque quantité imprime 1 ticket boisson + 1 repas + 1 dessert</small></p>
                            <div style={{ display: "flex", gap: 12, justifyContent: "center", alignItems: "center", margin: "16px 0" }}>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-article"
                                    onClick={() => setPrintQtyModal((prev) => ({
                                        ...prev,
                                        qty: Math.max(0, (Number(prev.qty) || 0) - 1),
                                    }))}
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    min={0}
                                    max={99}
                                    value={printQtyModal.qty}
                                    onChange={(event) => setPrintQtyModal((prev) => ({
                                        ...prev,
                                        qty: event.target.value,
                                    }))}
                                    style={{ width: 80, fontSize: "1.5rem", textAlign: "center" }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-article"
                                    onClick={() => setPrintQtyModal((prev) => ({
                                        ...prev,
                                        qty: Math.min(99, (Number(prev.qty) || 0) + 1),
                                    }))}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <div className="order-modal-footer" style={{ justifyContent: "center", gap: 8 }}>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={() => {
                                    setArticleAmount(printQtyModal.cIndex, printQtyModal.aIndex, 0);
                                    setPrintQtyModal(null);
                                }}
                            >
                                Retirer
                            </button>
                            <button type="button" className="btn btn-primary" onClick={confirmPrintQty}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashierPage;
